import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  RotateCcw,
  ShieldCheck,
  Users as UsersIcon,
  Star,
  X,
} from "lucide-react";
import Images from "../../../utils/Images";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import authService from "../../../Services/AuthService";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../../store/authSlice";
import { toast } from "react-toastify";

export default function VerifyCode() {
  const [code, setCode] = useState(["", "", "", ""]);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { email, phone, slug, message } = location.state || {};
  const dispatch = useDispatch();

  // Show message from sign-in redirect
  useEffect(() => {
    if (message) {
      toast.info(message);
    }
  }, [message]);

  // Redirect if no verification data
  useEffect(() => {
    if (!email && !phone && !slug) {
      navigate("/signup");
    }
  }, [email, phone, slug, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    } else setCanResend(true);
  }, [countdown]);

  const handleInputChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const next = [...code];
      next[index] = value;
      setCode(next);
      if (value && index < 3) inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").slice(0, 4);
    if (/^\d+$/.test(pasted)) {
      const arr = pasted.split("").concat(["", "", "", ""]).slice(0, 4);
      setCode(arr);
      const emptyIdx = arr.findIndex((d, i) => !d && i < 4);
      inputRefs.current[emptyIdx !== -1 ? emptyIdx : 3]?.focus();
    }
  };

  const isComplete = code.every((d) => d !== "");

  const handleVerify = async () => {
    if (!isComplete || !slug) {
      return;
    }

    setIsVerifying(true);
    setError("");
    setSuccess("");

    try {
      const otpCode = parseInt(code.join(""));
      const payload = {
        otp: otpCode,
        slug: slug,
      };

      const response = await authService.verifyAccount(payload);

      if (response.status) {
        setSuccess("Account verified successfully! Redirecting to sign in...");
        dispatch(
          loginSuccess({
            user: response.data?.data?.data,
            token: response.data?.data?.data.token,
          })
        );
        setTimeout(() => {
          navigate("/dashboard", {
            state: {
              message:
                "Account verified successfully! Please sign in to continue.",
            },
          });
        }, 2000);
      } else {
        setError(response.message || "Verification failed. Please try again.");
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Verification error:", error);
    } finally {
      setIsVerifying(false);
    }
  };
  const handleResend = async () => {
    if (!canResend || (!email && !phone)) {
      return;
    }

    setIsResending(true);
    setError("");
    setSuccess("");

    try {
      // Prioritize phone number over email for resend
      const payload = {
        email_or_phone: phone || email,
        channel: phone ? "phone" : "email",
      };

      const response = await authService.resendVerificationCode(payload);

      if (response.status) {
        setSuccess("Verification code resent successfully!");
        setCanResend(false);
        setCountdown(30);
        setCode(["", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        setError(response.message || "Failed to resend verification code.");
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
      console.error("Resend error:", error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <img
        src="https://images.unsplash.com/photo-1557804506-669a67965ba0?q=80&w=1600&auto=format&fit=crop"
        alt="bg"
        className="object-cover absolute inset-0 w-full h-full"
      />
      <div className="absolute inset-0 bg-black/60 pointer-events-none" />

      {/* Top bar with logo */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex relative z-10 justify-center items-start pt-10"
      >
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </motion.div>
      <button
        onClick={() => navigate(-1)}
        className="flex absolute top-4 left-4 z-20 items-center gap-2 px-3 py-2 text-xs font-medium text-white/90 rounded-lg border border-white/20 bg-white/10 backdrop-blur hover:bg-white/20"
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Glassmorphic marketing card */}
      <div className="absolute inset-x-0 top-28 z-10 px-6">
        <div className="grid gap-4 mx-auto max-w-4xl sm:grid-cols-3">
          {[
            { icon: UsersIcon, label: "1k+ agents" },
            { icon: ShieldCheck, label: "Secure payouts" },
            { icon: Star, label: "Trusted by fleets" },
          ].map((item, idx) => (
            <div
              key={idx}
              className="flex gap-3 justify-center items-center p-4 rounded-2xl border backdrop-blur-md border-white/10 bg-white/10 text-white/90"
            >
              <item.icon size={16} className="text-emerald-300" />
              <span className="text-xs">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Centered modal */}
      <div className="flex relative z-10 justify-center items-center p-6 min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="p-6 mt-10 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <h2 className="text-lg font-semibold text-center">
              Verify Your Account
            </h2>
            <p className="mt-1 text-xs text-center text-gray-500">
              Enter the 4-digit code sent to {phone || email}
            </p>
          </motion.div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative p-3 mt-4 text-sm text-red-700 bg-red-100 border border-red-300 rounded-lg"
            >
              <div className="pr-8">{error}</div>
              <button
                onClick={() => setError("")}
                className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-200 rounded-full transition-colors"
                aria-label="Close error message"
              >
                <X size={16} />
              </button>
            </motion.div>
          )}

          {/* Success Message */}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative p-3 mt-4 text-sm text-green-700 bg-green-100 border border-green-300 rounded-lg"
            >
              <div className="pr-8">{success}</div>
              <button
                onClick={() => setSuccess("")}
                className="absolute top-2 right-2 p-1 text-green-500 hover:text-green-700 hover:bg-green-200 rounded-full transition-colors"
                aria-label="Close success message"
              >
                <X size={16} />
              </button>
            </motion.div>
          )}

          <div className="mt-6 space-y-6">
            {/* Code Inputs */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="flex gap-3 justify-center"
            >
              {code.map((digit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 + index * 0.08, duration: 0.3 }}
                  className="relative group"
                >
                  <div className="relative rounded-full p-[2px] bg-gradient-to-br from-emerald-400/40 via-transparent to-emerald-400/40 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                    <motion.input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      value={digit}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      onPaste={index === 0 ? handlePaste : undefined}
                      whileFocus={{ scale: 1.05 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 25,
                      }}
                      className="w-14 h-14 text-lg font-bold text-center rounded-full border-0 outline-none bg-white/95 focus:text-emerald-700"
                      maxLength={1}
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Resend */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.3 }}
              className="text-center"
            >
              <AnimatePresence mode="wait">
                {canResend ? (
                  <motion.button
                    key="resend"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    whileHover={{ scale: isResending ? 1 : 1.05 }}
                    whileTap={{ scale: isResending ? 1 : 0.95 }}
                    onClick={handleResend}
                    disabled={isResending}
                    className="inline-flex gap-2 items-center text-xs font-medium text-emerald-600 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? (
                      <>
                        <div className="w-3 h-3 border-2 border-emerald-600/30 border-t-emerald-600 rounded-full animate-spin"></div>
                        Resending...
                      </>
                    ) : (
                      <>
                        <motion.div
                          animate={{ rotate: [0, 360] }}
                          transition={{ duration: 0.6, ease: "easeInOut" }}
                        >
                          <RotateCcw size={14} />
                        </motion.div>
                        Resend Code
                      </>
                    )}
                  </motion.button>
                ) : (
                  <motion.p
                    key="cd"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-xs text-gray-500"
                  >
                    Resend in <span className="font-medium">{countdown}</span>s
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Verify */}
            <motion.button
              onClick={() => {
                handleVerify();
              }}
              disabled={!isComplete || isVerifying}
              whileHover={{ scale: isComplete && !isVerifying ? 1.02 : 1 }}
              whileTap={{ scale: isComplete && !isVerifying ? 0.98 : 1 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                {isVerifying ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify Account
                    <motion.div
                      animate={isComplete ? { x: [0, 3, 0] } : { x: 0 }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <ArrowRight size={16} />
                    </motion.div>
                  </>
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full" />
            </motion.button>

            <div className="text-center">
              <Link
                to="/signup"
                className="block text-xs text-emerald-600 hover:text-emerald-700"
              >
                Back to roles
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
